import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Sparkles,
  Calendar,
  Layers,
  Scissors,
  PenTool,
  Tag,
  Boxes,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile (3 engagements + outro) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Logistique sur-mesure
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos cartons de déménagement {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Plus besoin de courir les magasins ou de surcharger le coffre de
            votre voiture. Notre service logistique de proximité prend tout
            en charge pour vous faire gagner un temps précieux {city.aCity}
            {" "}grâce à trois engagements clairs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Livraison à domicile intégrale",
              body: "Nous déposons tout votre matériel à plat directement devant votre porte, que vous habitiez à Guyancourt ou dans les villes voisines comme Versailles, Voisins-le-Bretonneux et Montigny-le-Bretonneux.",
              Icon: Truck,
            },
            {
              title: "Respect de votre planning",
              body: "Nous convenons ensemble d'un créneau horaire précis pour vous éviter d'attendre toute la journée et vous permettre d'attaquer vos colis immédiatement.",
              Icon: Calendar,
            },
            {
              title: "Matériel préservé",
              body: "Vos cartons vous sont remis dans un état impeccable, totalement secs, propres et prêts à l'emploi.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-8 max-w-3xl text-[15px] md:text-base text-[#3a4856] leading-relaxed">
          Cette logistique sur-mesure transforme radicalement vos
          préparatifs. En éliminant la corvée des déplacements, vous
          abordez votre changement de domicile l&apos;esprit plus léger et
          avec l&apos;assurance de disposer d&apos;un équipement de niveau
          professionnel dès le premier jour.
        </p>
      </div>
    </section>

    {/* ======= SECTION 3 — Tarifs compétitifs ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Tarifs compétitifs
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des tarifs compétitifs pour une qualité pro
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Il est tout à fait possible de sécuriser ses biens avec du
              matériel haut de gamme sans pour autant impacter lourdement
              le budget de son déménagement. Nous appliquons une politique
              tarifaire ultra-compétitive à destination des particuliers et
              des professionnels {city.aCity}, en proposant des prix
              directs d&apos;usine sur nos emballages à double cannelure.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Vous bénéficiez ainsi de la même qualité structurelle que
              celle utilisée par les experts de la logistique, mais à des
              tarifs transparents et dégressifs selon vos volumes.
              C&apos;est le compromis parfait pour obtenir des boîtes
              solides, indéformables et saines, tout en maîtrisant vos
              coûts de transition de A à Z.
            </p>

            <Link
              to="/cartons-demenagement"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-guyancourt-cta-shop"
            >
              <Tag className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons & tarifs
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Tag className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Prix directs d&apos;usine
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Cartons double cannelure professionnels au tarif fabricant.
              Tarifs dégressifs selon vos volumes pour particuliers comme
              pour professionnels {city.aCity}.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Qualité industrielle double cannelure
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Tarifs transparents et dégressifs
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Particuliers & professionnels
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Budget maîtrisé de A à Z
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Accessoires fermeture & manutention ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Fermeture & manutention
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Une gamme complète d&apos;accessoires de fermeture et de
            manutention
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour qu&apos;un carton offre sa résistance maximale et soit
            facile à manipuler, le choix des accessoires est tout aussi
            important que l&apos;emballage lui-même. Notre boutique en
            ligne met à votre disposition tous les outils indispensables
            pour sécuriser et fluidifier vos préparatifs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              step: "1",
              title: "Rouleau d'adhésif & dévidoir pistolet",
              body: "Ce duo incontournable, composé d'un ruban de 48 mm × 66 m et d'un pistolet ergonomique compatible, vous permet de sceller vos boîtes d'une seule main en quelques secondes, garantissant un verrouillage inviolable des rabats.",
              Icon: Scissors,
            },
            {
              step: "2",
              title: "Marqueur permanent à pointe biseautée",
              body: "L'accessoire indispensable pour appliquer notre conseil de marquage en écrivant lisiblement la pièce de destination et le contenu directement sur les faces visibles de vos cartons.",
              Icon: PenTool,
            },
            {
              step: "3",
              title: "Cutter à lame rétractable 18 mm",
              body: "Un outil sécurisé et robuste, parfait pour découper vos protections sur-mesure ou pour ouvrir vos colis en un clin d'œil dès votre arrivée sans déchirer le carton.",
              Icon: Scissors,
            },
            {
              step: "4",
              title: "Chariot de déménagement & diable pro",
              body: "Équipé d'une plateforme de 60 × 40 cm, le chariot s'associe au diable de manutention pour déplacer sans effort des piles de cartons ou vos appareils électroménagers les plus lourds, préservant ainsi votre santé.",
              Icon: Truck,
            },
          ].map(({ step, title, body, Icon }) => (
            <div
              key={step}
              className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <div className="absolute -top-4 left-5 flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white font-display font-extrabold text-[15px]">
                {step}
              </div>
              <div className="mt-3 flex items-center gap-2.5">
                <Icon className="h-5 w-5 text-[#237cb5]" strokeWidth={2.4} />
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 max-w-3xl bg-white border-l-4 border-[#237cb5] pl-5 py-2">
          <p className="text-[15px] md:text-base text-[#3a4856] leading-relaxed italic">
            L&apos;utilisation de ces équipements spécifiques change
            complètement la donne le jour du chargement. En centralisant vos
            fournitures de fermeture et de manutention sur notre plateforme,
            vous vous assurez que vos affaires quitteront {city.name}
            {" "}parfaitement protégées et prêtes pour un transfert rapide
            et sans fatigue vers votre nouveau lieu de vie.
          </p>
        </div>

        <div className="mt-8">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-guyancourt-cta-accessoires"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les accessoires de fermeture
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-guyancourt-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
